import { router } from 'expo-router';
import { useEffect, useState } from 'react';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import Animated, { useAnimatedRef } from 'react-native-reanimated';

import { VolunteerEvent } from '@/api/models/volunteerModels';
import { useAuth } from '@/contexts/authContext';
import { i18n } from '@/utils/i18n';

import BrandedButton from '../buttons/BrandedButton';
import ListEmptyComponent from '../ListEmptyComponent';
import { useScrollProps } from '../navigation/TopTabsReanimated';
import NextPageFetchingIndicator from '../NextPageFetchingIndicator';
import VolunteerCard from './VolunteerCard';

interface VolunteerAnimatedListProps {
  volunteerEvents: VolunteerEvent[] | undefined;
  location: {
    latitude: number;
    longitude: number;
  } | null;
  npoId: string;
  refetch: () => void;
  isFetchingNextPage: boolean;
  onEndReached: () => void;
  refreshing: boolean;
  canEdit: boolean;
}

const VolunteerAnimatedList = ({
  volunteerEvents,
  location,
  npoId,
  refetch,
  isFetchingNextPage,
  onEndReached,
  refreshing,
  canEdit
}: VolunteerAnimatedListProps) => {
  const { state } = useAuth();

  const { width } = useWindowDimensions();
  const flatRef = useAnimatedRef<Animated.FlatList<any>>();
  const { scrollEnabled, scrollHandler } = useScrollProps();

  const [numOfColumns, setNumOfColumns] = useState(3);

  useEffect(() => {
    if (width > 1200 && numOfColumns !== 3) {
      setNumOfColumns(3);
    } else if (width > 768 && width < 1200 && numOfColumns !== 2) {
      setNumOfColumns(2);
    } else if (width < 768 && numOfColumns !== 1) {
      setNumOfColumns(1);
    }
  }, [numOfColumns, width]);

  //Locales
  const createEvent = i18n.t('profile.create_event');

  return (
    <Animated.FlatList
      //Needed to force refresh of columns on the FlatList
      key={numOfColumns}
      ref={flatRef}
      numColumns={numOfColumns}
      onScroll={scrollHandler}
      scrollEnabled={scrollEnabled}
      showsVerticalScrollIndicator={false}
      bounces={false}
      data={volunteerEvents}
      style={style.flatList}
      {...(numOfColumns > 1 && {
        columnWrapperStyle: style.flatListColumnWrapperStyle
      })}
      contentContainerStyle={style.flatListContentContainerStyle}
      keyExtractor={(item: VolunteerEvent) => item.id}
      onEndReachedThreshold={0.2}
      onEndReached={onEndReached}
      refreshing={refreshing}
      renderItem={({ item }: { item: VolunteerEvent }) => (
        <View style={{ flex: 1 / numOfColumns }}>
          <VolunteerCard
            canEdit={canEdit}
            volunteerEvent={item}
            userLocation={location}
            onCardPress={() => {
              router.navigate({
                pathname: '/volunteerDetail/[volunteerId]',
                params: {
                  volunteerId: item.id
                }
              });
            }}
            onDelete={refetch}
          />
        </View>
      )}
      {...(canEdit && {
        ListHeaderComponentStyle: style.fullWidth,
        ListHeaderComponent: (
          <BrandedButton
            onPress={() => {
              router.navigate({
                pathname: '/create/volunteerEvent',
                params: {
                  npoId: npoId ?? state.signUpData?.loginEmail?.npoId
                }
              });
            }}
            style={style.createButton}>
            {createEvent}
          </BrandedButton>
        )
      })}
      ListEmptyComponent={<ListEmptyComponent label={i18n.t('npo_tab.empty_volunteer')} />}
      ListFooterComponent={isFetchingNextPage ? <NextPageFetchingIndicator /> : null}
    />
  );
};

export default VolunteerAnimatedList;

const style = StyleSheet.create({
  fullWidth: {
    width: '100%'
  },

  flatList: {
    flex: 1,
    width: '100%',
    paddingBottom: 26
  },

  //Vertical Gap for flatList
  flatListContentContainerStyle: {
    flex: 1,
    gap: 27
  },

  //Horizontal Gap for flatList
  flatListColumnWrapperStyle: {
    flex: 1,
    gap: 22
  },

  createButton: {
    width: '100%',
    marginTop: 5,
    marginBottom: 25
  }
});
